import { SerializedStyles } from "@emotion/react";
import { Ref, memo, useCallback } from "react";
import { useForm } from "react-hook-form";
import { Checkbox, FormFlowPage, TextInputWithLoading } from "src/components";
import useMergedRef from "src/hooks/useMergedRef";
import asyncZodResolver from "src/utilities/asyncZodResolver";
import { noInput } from "src/utilities/input";
import { strictEmailValidate } from "src/validators";
import { z } from "zod";

import { notUndefinedOrNull } from "@fraction/shared";

import { usePartialSubmit } from "../hooks";
import { styles } from "../styles";

export interface ApplicantEmailProps {
  value?: { email?: string; optedIn?: Date | null };
  onChange?: (value?: { email?: string; optedIn?: Date | null }) => void;

  focusRef?: Ref<HTMLInputElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
}

const SCHEMA = z.object({
  applicantEmail: strictEmailValidate,
});

const ApplicantEmail = ({
  value,
  onChange,
  onNext,
  onBack,
  style,
  focusRef,
  ...props
}: ApplicantEmailProps) => {
  const validate = useCallback(() => !noInput(value?.email), [value]);
  const { submitHandler, nextDisabled, nextLoading } = usePartialSubmit({ onNext, validate });

  const { register, handleSubmit, errors, formState } = useForm({
    resolver: asyncZodResolver(SCHEMA),
    reValidateMode: "onSubmit",
  });

  const handleChangeEmail = useCallback(
    (email: string) => {
      onChange?.({ ...value, email });
    },
    [onChange, value]
  );

  const handleChangeOptIn = useCallback(
    (checked: boolean) => {
      onChange?.({ ...value, optedIn: notUndefinedOrNull(checked) ? new Date() : null });
    },
    [onChange, value]
  );

  const merged = useMergedRef(register, focusRef);
  const ref = focusRef ? merged : register;

  return (
    <FormFlowPage
      {...props}
      title="Where should we email you?"
      description="This is how we’ll reach out to you regarding your application."
      onNext={handleSubmit(submitHandler)}
      onBack={onBack}
      style={style}
      nextDisabled={nextDisabled}
      nextLoading={nextLoading}
    >
      <TextInputWithLoading
        error={errors?.applicantEmail?.message}
        ref={ref}
        style={styles.input}
        name="applicantEmail"
        label="Email address"
        placeholder="contact@fraction.com"
        defaultValue={value?.email}
        onChange={handleChangeEmail}
        type="email"
        autoComplete="email"
        loading={formState.isValidating || formState.isSubmitting}
      />
      <Checkbox
        labelStyle={styles.checkboxLabelSmall}
        style={styles.checkbox}
        checked={notUndefinedOrNull(value?.optedIn)}
        onChange={handleChangeOptIn}
        label="Opt-in to receive email updates from Fraction about products, events, and resources. You can opt-out at any time."
      />
    </FormFlowPage>
  );
};

export default memo(ApplicantEmail);
